import React, { useEffect, useMemo, useState } from "react";
import {
  corporateSignupCheckSchoolId,
  corporateSignupStart,
  corporateSignupVerify,
} from "./corporateSignupApi";

const SUCCESS_MESSAGE =
  "Your 7-day school trial has been created. You can now log in using your verified email.";

const SUBSCRIPTION_PLANS = [
  {
    plan: "7-Day Free Trial",
    recommendedFor: "Evaluate Articulate with your teachers",
    price: "Free",
    notes: "No credit card required",
  },
  {
    plan: "Starter",
    recommendedFor: "~30 students",
    price: "S$300 / 3 months",
    notes: "≈ S$100/month",
  },
  {
    plan: "Standard",
    recommendedFor: "~150 students",
    price: "S$1,350 / 3 months",
    notes: "≈ S$450/month",
  },
  {
    plan: "Premium",
    recommendedFor: "Large schools",
    price: "S$2,400 / 3 months",
    notes: "≈ S$800/month",
  },
];

function getGeneratedSchoolId(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

function getErrorMessage(error, fallback) {
  return error?.data?.message || error?.data?.error || error?.message || fallback;
}

function CorporateSignupForm({ compact = false, onResize }) {
  const [step, setStep] = useState(1);
  const [organisationName, setOrganisationName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [schoolIdPreview, setSchoolIdPreview] = useState("");
  const [serverSchoolId, setServerSchoolId] = useState("");
  const [otp, setOtp] = useState("");
  const [startResponse, setStartResponse] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const generatedSchoolId = useMemo(
    () => getGeneratedSchoolId(organisationName),
    [organisationName]
  );
  const visibleSchoolId = schoolIdPreview || serverSchoolId || generatedSchoolId;

  useEffect(() => {
    if (!organisationName.trim()) {
      setSchoolIdPreview("");
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const data = await corporateSignupCheckSchoolId(organisationName.trim(), {
          signal: controller.signal,
        });
        setSchoolIdPreview(data?.school_id || data?.suggested_school_id || "");
      } catch (_) {
        setSchoolIdPreview("");
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [organisationName]);

  useEffect(() => {
    if (onResize) onResize();
  }, [step, status, error, success, visibleSchoolId, onResize]);

  async function handleStart(event) {
    event.preventDefault();
    setError("");
    setStatus("");

    const trimmedName = organisationName.trim();
    const trimmedAdminEmail = adminEmail.trim();

    if (!trimmedName || !trimmedAdminEmail) {
      setError("School or organisation name and admin email are required.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        school_name: trimmedName,
        organisation_name: trimmedName,
        admin_email: trimmedAdminEmail,
        billing_email: billingEmail.trim() || undefined,
        contact_name: contactName.trim() || undefined,
        phone: phone.trim() || undefined,
      };
      const data = await corporateSignupStart(payload);
      setStartResponse(data || {});
      setServerSchoolId(data?.school_id || data?.suggested_school_id || "");
      setStatus("Verification code sent. Please check the admin email inbox.");
      setStep(2);
    } catch (err) {
      setError(getErrorMessage(err, "Could not start corporate signup."));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(event) {
    event.preventDefault();
    setError("");
    setStatus("");

    if (!otp.trim()) {
      setError("Please enter the OTP sent to the admin email.");
      return;
    }

    setSubmitting(true);
    try {
      await corporateSignupVerify({
        token: startResponse?.token,
        otp: otp.trim(),
      });
      setSuccess(true);
      setStatus(SUCCESS_MESSAGE);
    } catch (err) {
      setError(getErrorMessage(err, "Could not verify the OTP."));
    } finally {
      setSubmitting(false);
    }
  }

  const pageStyle = {
    width: "100%",
    maxWidth: compact ? 560 : 720,
    margin: compact ? "0 auto" : "24px auto",
    padding: compact ? 16 : 24,
    boxSizing: "border-box",
    textAlign: "left",
  };

  const cardStyle = {
    background: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    boxShadow: compact ? "none" : "0 6px 18px rgba(0, 0, 0, 0.08)",
    padding: compact ? 18 : 28,
  };

  const fieldStyle = {
    display: "grid",
    gap: 6,
  };

  const inputStyle = {
    width: "100%",
    minHeight: 44,
    border: "1px solid #cfd4dc",
    borderRadius: 6,
    padding: "10px 12px",
    boxSizing: "border-box",
    fontSize: 16,
  };

  const buttonStyle = {
    width: "100%",
    minHeight: 46,
    borderRadius: 6,
    fontSize: 16,
  };

  const tableCellStyle = {
    borderBottom: "1px solid #e5e7eb",
    padding: "12px 10px",
    textAlign: "left",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  };

  if (success) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={{ marginTop: 0, fontSize: compact ? 24 : 30 }}>
            School Trial Created
          </h1>
          <p style={{ lineHeight: 1.55, color: "#2e7d32", fontWeight: 700 }}>
            {SUCCESS_MESSAGE}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={{ marginTop: 0, fontSize: compact ? 24 : 30 }}>
          Corporate School Signup
        </h1>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              height: 6,
              borderRadius: 999,
              background: step >= 1 ? "#3F51B5" : "#d8dce8",
            }}
          />
          <div
            style={{
              height: 6,
              borderRadius: 999,
              background: step >= 2 ? "#3F51B5" : "#d8dce8",
            }}
          />
        </div>

        {step === 1 ? (
          <>
            <section style={{ marginBottom: 24 }}>
              <h2 style={{ margin: "0 0 12px", fontSize: compact ? 20 : 22 }}>
                Subscription Plans
              </h2>
              <div
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  overflowX: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    minWidth: 620,
                    borderCollapse: "collapse",
                    fontSize: 15,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f6f8ff", color: "#27347b" }}>
                      <th style={tableCellStyle}>Plan</th>
                      <th style={tableCellStyle}>Recommended For</th>
                      <th style={tableCellStyle}>Price</th>
                      <th style={tableCellStyle}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SUBSCRIPTION_PLANS.map((plan) => (
                      <tr key={plan.plan}>
                        <td style={{ ...tableCellStyle, fontWeight: 700 }}>{plan.plan}</td>
                        <td style={tableCellStyle}>{plan.recommendedFor}</td>
                        <td style={tableCellStyle}>{plan.price}</td>
                        <td style={tableCellStyle}>{plan.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ margin: "10px 0 0", color: "#6b7280", fontSize: 14, lineHeight: 1.45 }}>
                Paid subscriptions are prepaid for the selected duration. Monthly equivalent is shown for comparison only. Contact us for 6- and 12-month pricing or custom enterprise plans.
              </p>
            </section>

            <form onSubmit={handleStart} style={{ display: "grid", gap: 16 }}>
              <div style={fieldStyle}>
                <label>School / organisation name</label>
                <input
                  style={inputStyle}
                  value={organisationName}
                  onChange={(event) => setOrganisationName(event.target.value)}
                  autoComplete="organization"
                  required
                />
              </div>

              <div style={fieldStyle}>
                <label>Admin email</label>
                <input
                  style={inputStyle}
                  type="email"
                  value={adminEmail}
                  onChange={(event) => setAdminEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div style={fieldStyle}>
                  <label>Billing email optional</label>
                  <input
                    style={inputStyle}
                    type="email"
                    value={billingEmail}
                    onChange={(event) => setBillingEmail(event.target.value)}
                    autoComplete="email"
                  />
                </div>

                <div style={fieldStyle}>
                  <label>Contact name optional</label>
                  <input
                    style={inputStyle}
                    value={contactName}
                    onChange={(event) => setContactName(event.target.value)}
                    autoComplete="name"
                  />
                </div>
              </div>

              <div style={fieldStyle}>
                <label>Phone optional</label>
                <input
                  style={inputStyle}
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  autoComplete="tel"
                />
              </div>

              {visibleSchoolId ? (
                <div
                  style={{
                    background: "#f6f8ff",
                    border: "1px solid #d8def8",
                    borderRadius: 8,
                    color: "#27347b",
                    padding: 12,
                    wordBreak: "break-word",
                  }}
                >
                  <strong>School ID preview:</strong> {visibleSchoolId}
                </div>
              ) : null}

              {error ? <div style={{ color: "#c62828", fontWeight: 700 }}>{error}</div> : null}
              {status ? <div style={{ color: "#2e7d32", fontWeight: 700 }}>{status}</div> : null}

              <button type="submit" style={buttonStyle} disabled={submitting}>
                {submitting ? "Sending OTP..." : "Start 7-day free trial"}
              </button>
            </form>
          </>
        ) : (
          <form onSubmit={handleVerify} style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                lineHeight: 1.5,
              }}
            >
              OTP sent to <strong>{adminEmail}</strong>.
            </div>

            <div style={fieldStyle}>
              <label>OTP</label>
              <input
                style={{ ...inputStyle, textAlign: "center", letterSpacing: 0, fontSize: 22 }}
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </div>

            {visibleSchoolId ? (
              <div
                style={{
                  background: "#f6f8ff",
                  border: "1px solid #d8def8",
                  borderRadius: 8,
                  color: "#27347b",
                  padding: 12,
                  wordBreak: "break-word",
                }}
              >
                <strong>School ID:</strong> {visibleSchoolId}
              </div>
            ) : null}

            {error ? <div style={{ color: "#c62828", fontWeight: 700 }}>{error}</div> : null}
            {status ? <div style={{ color: "#2e7d32", fontWeight: 700 }}>{status}</div> : null}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button
                type="button"
                style={{ ...buttonStyle, background: "#e5e7eb", color: "#111827" }}
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                Back
              </button>
              <button type="submit" style={buttonStyle} disabled={submitting}>
                {submitting ? "Verifying..." : "Verify OTP"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CorporateSignupForm;
